<ng-container *transloco="let convert;">
  @if (offerHistory) {
    @if (rootOffer && showSummary) {
      <!--
        M4.14: the rules above and below the history are dropped for all-in users, per the comp
        (Figma 11024:11203), which separates the summary strip, the history disclosure and the breakdown
        by whitespace alone.

        Gated on `collapsible`, not on a DripPricingService injection. That input already IS the drip
        gate: it defaults to false and all three hosts that pass it -- counter-offer, withdraw-offer and
        counter-offer-refactor -- bind it to their own showDripBreakdown, so collapsible === true implies
        buy-allInPricingEnabled is on. Injecting the service here would add a second, independent source
        of truth for the same question and let the two disagree; reusing the input cannot. The other
        hosts, and the bidHistory branch below, keep today's borders untouched.
      -->
      <div class="offer-details" [class.offer-details--flush]="collapsible">
        <div class="offer-details__item">
          <div class="offer-details__item-label">{{ convert('vehicles.offerHistory.textVIN') }}</div>
          <div class="offer-details__item-value">{{ vin }}</div>
        </div>
        <div class="offer-details__item">
          <div class="offer-details__item-label">{{ convert('vehicles.offerHistory.textDate') }}</div>
          <div class="offer-details__item-value">{{ localizeDate(rootOffer.offerDate) }}</div>
        </div>
        <div class="offer-details__item">
          <div class="offer-details__item-label">{{ convert('vehicles.offerHistory.textAmount') }}</div>
          <div class="offer-details__item-value">{{ formatCurrency(rootOffer.amount) }}</div>
        </div>
        <div class="offer-details__item">
          <div class="offer-details__item-label">{{ convert('vehicles.offerHistory.textStatus') }}</div>
          <div class="offer-details__item-value">{{ convert(fakeOfferStatus | offerStatus) }}</div>
        </div>
      </div>
    }
    <div class="offer-chain" [class.offer-chain--flush]="collapsible">
      <!--
        M4.8: the comps collapse the history behind a disclosure so the all-in breakdown and the
        binding-offer prose are both above the fold.

        Opt-in per host (`collapsible`, default false) rather than a rewrite: seven templates render
        this component, and only the two View Offer modals were redesigned. Everywhere else the title
        and the chain stay exactly as they are, including the role="alert" section -- which is why the
        heading is duplicated across the two branches instead of being hoisted. A <button> cannot carry
        role="alert", and the collapsed variant must not announce itself on render the way the
        always-expanded one does.
      -->
      @if (collapsible) {
        <button
          type="button"
          id="vom_offer_history_toggle_button"
          class="offer-chain__toggle btn btn-link"
          (click)="toggleHistory()"
          [attr.aria-expanded]="historyExpanded"
          aria-controls="vom_offer_history_panel"
          data-track-elem="accordion"
          data-track-trigger="statechange"
          data-track-name="Offer History"
          [attr.data-track-state]="historyExpanded ? 'open' : 'closed'"
          data-test-id="app-offer-history-e-toggle-button"
          >
          <i
            class="allycon-glyph-caret-right offer-chain__toggle-icon"
            [class.expanded]="historyExpanded"
            aria-hidden="true"
          ></i>
          <!--
            textOfferHistory, not textHistory: the comps label this disclosure "Offer History", and
            textHistory is "History:" -- trailing colon and all, because every other use of it is a
            heading immediately followed by the chain. That key is shared with the @else branch below
            and with bid-history, so editing it in place would retitle seven surfaces that were never
            redesigned. A second key is the smaller change.
          -->
          <span class="offer-chain__toggle-title">{{ convert('vehicles.offerHistory.textOfferHistory') }}</span>
        </button>
      } @else {
        <section role="alert">
          <div class="offer-chain__title" id="vom_offer_history_text_blob">{{ convert('vehicles.offerHistory.textHistory') }}</div>
        </section>
      }
      <!--
        The panel is always in the DOM and hidden with display, not removed with @if: aria-controls has
        to resolve to a real element for the disclosure to be announced correctly, and a removed panel
        makes it dangle. Non-collapsible hosts render it open, as today.
      -->
      <div id="vom_offer_history_panel" [style.display]="collapsible && !historyExpanded ? 'none' : null">
      @for (date of previousOffers?.dates; track date) {
        <div class="offer-chain__date">
          <span class="offer-chain__date-text">{{ localizeDate(date) }}</span>
        </div>
        @for (offer of previousOffers.offersByDate[date]; track offer; let even = $even) {
          <div
            id="vom_offer_history_text_blob"
            class="offer-chain--details"
            [ngClass]="even ? 'row-even' : 'row-odd'"
            tabindex="0"
            >
            <div class="row space-between">
              <p class="bold">{{ convert(offer.role | parseString) }}</p>
              <p>{{ formatTime(offer.offerDate) }}</p>
            </div>
            <p><span class="fw-bold">{{ convert('vehicles.offerHistory.textStatus') | titlecase }}</span> {{ convert(offer.status | offerStatus) }}</p>
            <p><span class="fw-bold">{{ convert('vehicles.offerHistory.textOfferAmount') }}</span> {{ formatCurrency(offer.amount) }}</p>
            <div class="offer-chain--comments">
              <p>
                <span class="fw-bold">{{ convert('vehicles.offerHistory.textComment') }}</span>
                <span class="fst-italic">{{ offer.comment }}</span>
              </p>
            </div>
          </div>
        }
      }
      </div>
    </div>
    <!--
      Slot for the all-in breakdown (M4.8), BELOW the chain.

      M4.20 moved it. It was introduced above the chain on the reading that the comps put the breakdown
      directly after VIN/DATE/AMOUNT/STATUS; they do not. Both the Withdraw comp (Figma 10493:16426) and
      the Accept comp (10503:24898) order the modal disclosure-first: summary strip, then the "Offer
      History" toggle, then the expanded chain, then the breakdown. In 10503:24898 the toggle frame sits
      at y=96 and the frame carrying the breakdown at y=136, so the breakdown is unambiguously last.

      Still a slot rather than a host-side sibling: the strip and the chain are both this component's
      markup, so a host cannot position itself relative to them by ordering its own children.

      Empty for the eight hosts that project nothing -- withdraw-offer is the only one that fills it, and
      Angular drops unmatched content silently -- so an unslotted host renders exactly as before and this
      move is invisible to all of them.
    -->
    <ng-content select="[appOfferHistoryAfterSummary]"></ng-content>
  }

  @if (bidHistory) {
    <div class="offer-details">
      <div class="offer-details__item">
        <div class="offer-details__item-label">{{convert('vehicles.offerHistory.textVIN')}}</div>
        <div class="offer-details__item-value">{{ vin }}</div>
      </div>
    </div>
    <div class="offer-chain">
      <div class="offer-chain__title">{{ convert('vehicles.offerHistory.textHistory') | titlecase }}</div>
      @for (item of bidHistory | keyvalue; track item) {
        <div>
          <div class="offer-chain__date">
            <span class="offer-chain__date-text">{{ localizeDate(item.key)}} </span>
          </div>
          @for (bid of bidHistory[item.key]; track bid; let even = $even) {
            <div
              class="offer-chain--details"
              [ngClass]="even ? 'row-even' : 'row-odd'"
              >
              <div class="row space-between">
                <p><span class="fw-bold">{{ convert('vehicles.offerHistory.DealershipName') }}</span> {{ bid.buyerOeName | parseString }}</p>
                <p>{{ formatTime(bid.bidDate) }}</p>
              </div>
              <p><span class="fw-bold">{{ convert('vehicles.offerHistory.textBidAmount') }}</span> {{ formatCurrency(bid.bidPrice) }}</p>
              <p><span class="fw-bold">{{ convert('vehicles.offerHistory.textBidType') }}</span> {{ bid.bidType | parseString}}</p>
              <p><span class="fw-bold">{{ convert('vehicles.offerHistory.textStatus') | titlecase }}</span> {{ bid.bidStatus | parseString }}</p>
            </div>
          }
        </div>
      }
    </div>
  }
</ng-container>
